import { Helmet } from "@/pages/home/components/Helmet";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useState } from "react";
import { testimonials } from "@/mocks/comics";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== "") {
      setFormStatus("success");
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch("http://uqdww.cn/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setFormError("");
        form.reset();
      } else if (serverMsg && (typeof serverMsg === "string") && (serverMsg.includes("spam") || serverMsg.includes("form data is spam"))) {
        setFormStatus("error");
        setFormError(serverMsg);
      } else {
        setFormStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络异常，请检查连接后重试");
    }
  };

  return (
    <>
      <Helmet
        title="关于我们-韩漫头条-免费韩国漫画在线阅读平台"
        description="韩漫头条是专注于韩国漫画在线阅读的中文平台。了解我们的品牌故事、平台优势与联系方式。提供无删减韩国漫画下拉式阅读，免费韩漫大全。"
        keywords="韩漫头条,关于我们,韩国漫画平台,免费韩漫,联系我们"
        canonicalUrl="http://uqdww.cn/about"
        ogTitle="关于我们 - 韩漫头条"
        ogDescription="韩漫头条是专注于韩国漫画在线阅读的中文平台。了解我们的品牌故事与联系方式。"
        schemaJsonLd={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "关于我们 - 韩漫头条",
          "description": "韩漫头条是专注于韩国漫画在线阅读的中文平台，提供无删减韩国漫画下拉式阅读。",
          "url": "http://uqdww.cn/about",
          "isPartOf": {
            "@type": "WebSite",
            "name": "韩漫头条",
            "url": "http://uqdww.cn/"
          }
        }}
      />
      <Navbar />
      <main className="pt-20">
        {/* Breadcrumb */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="container-page">
            <Breadcrumb items={[{ label: "关于我们" }]} />
          </div>
        </section>

        {/* Brand Intro */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="container-page">
            <div className="flex flex-col md:flex-row gap-10 md:gap-16 items-center">
              <div className="flex-1">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1 rounded-full mb-4">
                  <i className="ri-building-4-line"></i> 关于我们
                </span>
                <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
                  打造最优质的
                  <span className="text-primary-500">韩国漫画</span>
                  <br />
                  阅读平台
                </h1>
                <p className="mt-5 text-foreground-600 text-sm md:text-base leading-relaxed max-w-lg">
                  韩漫头条成立于2023年，致力于为全球华语用户提供最新、最全的韩国漫画在线阅读服务。
                  我们坚持「免费、无删减、高品质」的原则，覆盖恋爱、奇幻、动作、悬疑等热门题材，
                  让每一位韩漫爱好者都能在这里找到属于自己的精神乐园。
                </p>
                <div className="grid grid-cols-3 gap-4 mt-8">
                  {[
                    { num: "500+", label: "漫画作品" },
                    { num: "8", label: "题材分类" },
                    { num: "1000万+", label: "月活用户" },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center">
                      <p className="font-heading text-2xl md:text-3xl text-primary-500">{stat.num}</p>
                      <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-1">
                <div className="bg-primary-50 rounded-2xl p-6 md:p-10 relative">
                  <div className="absolute top-4 left-4 text-primary-300/50 text-xs font-medium">品牌使命</div>
                  <i className="ri-double-quotes-l text-5xl text-primary-300/40 block mb-4"></i>
                  <p className="text-lg md:text-xl text-primary-700 leading-relaxed font-medium">
                    「让每一位韩漫爱好者都能自由畅享漫画世界的精彩，不受语言和地域的限制。」
                  </p>
                  <div className="flex items-center gap-3 mt-6">
                    <div className="w-10 h-10 rounded-full bg-primary-200 flex items-center justify-center text-primary-600 font-heading text-sm font-bold">
                      韩
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-primary-800">韩漫头条团队</p>
                      <p className="text-xs text-primary-500">全力以赴，只为更好的阅读体验</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Advantages */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="container-page">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-500 bg-accent-50 px-3 py-1 rounded-full mb-4">
                <i className="ri-shield-check-line"></i> 平台优势
              </span>
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">
                为什么选择 <span className="text-accent-500">韩漫头条</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                { icon: "ri-shield-check-line", title: "无删减原版", desc: "所有漫画均为无删减版本，保留完整剧情内容，让你看到原汁原味的韩国漫画。" },
                { icon: "ri-smartphone-line", title: "下拉式阅读", desc: "采用先进的下拉式阅读技术，无需翻页，流畅滑动即可沉浸式阅读。" },
                { icon: "ri-speed-up-line", title: "极速同步更新", desc: "与原版同步更新，通常24小时内完成中文翻译并上线，追番不等待。" },
                { icon: "ri-hd-line", title: "高清画质", desc: "所有漫画均为高清画质呈现，细节清晰，色彩还原度高，视觉体验一流。" },
              ].map((advantage, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full bg-accent-50 text-accent-500">
                    <i className={`${advantage.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-950 font-semibold mb-2">{advantage.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{advantage.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="py-16 md:py-24 bg-background-200/70">
          <div className="container-page">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1 rounded-full mb-4">
                <i className="ri-chat-heart-line"></i> 用户好评
              </span>
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">
                万千读者的 <span className="text-primary-500">共同选择</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {testimonials.slice(0, 3).map((t) => (
                <div key={t.id} className="bg-background-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>
                    ))}
                  </div>
                  <i className="ri-double-quotes-l text-2xl text-primary-200 mb-2 block"></i>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="container-page">
            <div className="flex flex-col lg:flex-row gap-10 md:gap-16">
              <div className="lg:w-1/2">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary-600 bg-secondary-100 px-3 py-1 rounded-full mb-4">
                  <i className="ri-mail-send-line"></i> 联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 leading-tight">
                  有任何问题？
                  <br />
                  <span className="text-secondary-500">随时联系我们</span>
                </h2>
                <p className="mt-4 text-foreground-500 text-sm leading-relaxed max-w-md">
                  无论是合作洽谈、内容建议还是使用问题，我们都非常乐意听到你的声音。
                  填写表单后我们会在24小时内回复。
                </p>

                <div className="mt-8 flex flex-col gap-4">
                  {[
                    { icon: "ri-phone-line", label: "电话", value: "+86 10-8888-6666" },
                    { icon: "ri-mail-line", label: "邮箱", value: "hello@uqdww.cn" },
                    { icon: "ri-map-pin-line", label: "地址", value: "北京市朝阳区望京SOHO T1栋B座1806室" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500 shrink-0">
                        <i className={`${item.icon} text-lg`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">{item.label}</p>
                        <p className="text-sm text-foreground-950 font-medium">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:w-1/2">
                <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                  <h3 className="font-heading text-xl text-foreground-950 mb-6">发送消息</h3>
                  <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <input
                      type="text"
                      name="company_alt"
                      className="anti-spam-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="ab-name" className="block text-xs text-foreground-600 mb-1.5 font-medium">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="ab-name"
                        name="name"
                        type="text"
                        required
                        placeholder="请输入你的姓名"
                        className="w-full bg-background-50 border border-background-200/70 rounded-lg px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="ab-email" className="block text-xs text-foreground-600 mb-1.5 font-medium">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="ab-email"
                        name="email"
                        type="email"
                        required
                        placeholder="请输入你的邮箱"
                        className="w-full bg-background-50 border border-background-200/70 rounded-lg px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="ab-subject" className="block text-xs text-foreground-600 mb-1.5 font-medium">
                        主题
                      </label>
                      <input
                        id="ab-subject"
                        name="subject"
                        type="text"
                        placeholder="请输入主题"
                        className="w-full bg-background-50 border border-background-200/70 rounded-lg px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="ab-message" className="block text-xs text-foreground-600 mb-1.5 font-medium">
                        留言 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="ab-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        placeholder="请输入你的留言（500字以内）"
                        className="w-full bg-background-50 border border-background-200/70 rounded-lg px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-6 py-3 rounded-full whitespace-nowrap cursor-pointer transition-all flex items-center justify-center gap-2"
                    >
                      <i className="ri-send-plane-line"></i> 发送消息
                    </button>
                    {formStatus === "success" && (
                      <p className="text-sm text-green-600 bg-green-50 px-4 py-2 rounded-lg">
                        <i className="ri-check-line mr-1"></i>消息已发送，我们会尽快回复！
                      </p>
                    )}
                    {formStatus === "error" && (
                      <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">
                        <i className="ri-error-warning-line mr-1"></i>{formError || "提交失败，请稍后重试"}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}